import classNames from './PopUpSummary.module.scss';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/store';
import { GameTypes, NewGameTypes, PopUpSummaryProps } from '@/types/game';
import UseScreenSize from '@/hooks/UseScreenSize';
import { gameSelector } from '@/store/reducers/game';

import QuizSummary from './QuizSummary/QuizSummary';
import PracticeSummary from './PracticeSummary/PracticeSummary';
import GameUtils from '@/utils/gameUtils';

const PopUpSummary = ({
  className,
  selectedSkill,
  selectedTopic,
  selectedQuiz,
  coins,
  tip,
  userData,
  correctAnswers,
  totalQuestions,
  hasNextQuiz,
  isLastActivityForCanDo,
  emitOnClosePopUp,
  emitOnExit,
  emitOnAknowledge,
  emitStartQuizFromPractice,
  emitPracticeAgain,
  emitOnQuit,
  emitOnEndOfSkillTest,
  emitOnExitWithCanDo,
}: PopUpSummaryProps) => {
  const { t } = useTranslation();
  const { isMobile } = UseScreenSize();
  const {
    gameType: gameTypeReal,
    quizQuestionResultData,
    gameQuestions,
    isOpenWritingAnswerInEnglish,
  } = useAppSelector(gameSelector);

  const isRead = gameTypeReal === NewGameTypes.Read;
  const isOpenWriting = gameTypeReal === NewGameTypes.OpenWriting;
  let averageScore: number | null = null;
  let noGrading: boolean = false;
  let isSkipped: boolean = false;

  // Extract common NaturalConversation logic
  const isNaturalConversation = gameTypeReal === NewGameTypes.NaturalConversation;
  const speechQuestions = isNaturalConversation
    ? quizQuestionResultData.filter(obj => 'questionInputStatus' in obj)
    : [];
  const allSpeechNoInput =
    speechQuestions.length > 0 &&
    speechQuestions.every(obj => obj.questionInputStatus === 'NoInput');
  const multichoicePart = isNaturalConversation
    ? quizQuestionResultData.filter(
        obj => obj.gameType === NewGameTypes.NaturalConversation && !('questionInputStatus' in obj)
      )
    : [];

  if (isOpenWriting) {
    const gradedResults = quizQuestionResultData.filter(
      result => result.openWritingResult?.scoreResponse?.noGrading !== true
    );

    // Check if there are any issues (vulgar language, gibberish, non-English) in any open writing responses
    const hasIssues = quizQuestionResultData.some(result => {
      const openWritingResult = result.openWritingResult;
      if (!openWritingResult) return false;

      const scoreResponse = openWritingResult.scoreResponse;
      const question = gameQuestions.find(q => q.questionId === result.questionId);
      const isResponseInEnglish = question?.newData?.data?.metadata?.isResponseInEnglish;

      return (
        scoreResponse?.containsVulgarLanguage ||
        scoreResponse?.containsGibberish ||
        (isResponseInEnglish && !isOpenWritingAnswerInEnglish)
      );
    });

    if (gradedResults.length === 0) {
      // Only set noGrading to true if there are no issues
      noGrading = !hasIssues;
      averageScore = null;
    } else {
      const scores: number[] = gradedResults
        .map(result => result.openWritingResult?.scoreResponse?.overallScore)
        .filter((score): score is number => score != null);

      const totalScores: number = scores.reduce((sum, score) => sum + score, 0);

      averageScore = scores.length > 0 ? totalScores / scores.length : null;
    }
  } else {
    let totalNumberOfQuestions = 0;
    if (isRead) {
      totalNumberOfQuestions = GameUtils.getTotalNumberOfQuestionsWithScore(gameQuestions);
    }
    averageScore = GameUtils.calculateScore(quizQuestionResultData, totalNumberOfQuestions);
  }

  if (gameTypeReal === NewGameTypes.Speak) {
    const isActivitySkipped = GameUtils.isSkipped(quizQuestionResultData);

    if (isActivitySkipped) {
      isSkipped = true;
      averageScore = null;
    }
  }

  const isPractice = gameTypeReal === GameTypes.Practice;
  const isFailed = GameUtils.isFailed(averageScore ?? 0);
  const showCoins = isFailed ? 0 : coins;

  // Calculate correct total questions for display
  let displayTotalQuestions = totalQuestions;
  if (isNaturalConversation && !isSkipped && allSpeechNoInput) {
    displayTotalQuestions = multichoicePart.length;
  }

  return (
    <div
      className={classes(
        {
          container: isMobile,
        },
        className,
        classNames.popUpSummary
      )}
    >
      {isMobile && (
        <div className={classNames.exitBtn}>
          <button onClick={emitOnExit} aria-label={t('exit').toString()}>
            {t('exit')}
          </button>
        </div>
      )}
      {isPractice ? (
        <PracticeSummary
          emitOnAknowledge={emitOnAknowledge}
          emitStartQuizFromPractice={emitStartQuizFromPractice}
          emitPracticeAgain={emitPracticeAgain}
          emitOnQuit={emitOnQuit}
        />
      ) : (
        <QuizSummary
          selectedQuiz={selectedQuiz}
          selectedTopic={selectedTopic}
          selectedSkill={selectedSkill}
          coins={showCoins}
          tip={tip}
          hasNextQuiz={hasNextQuiz}
          userData={userData}
          correctAnswers={correctAnswers}
          totalQuestions={displayTotalQuestions}
          totalScore={averageScore}
          isOpenWriting={isOpenWriting}
          noGrading={noGrading}
          isSkipped={isSkipped}
          isLastActivityForCanDo={isLastActivityForCanDo}
          emitOnClosePopUp={emitOnClosePopUp}
          emitOnEndOfSkillTest={emitOnEndOfSkillTest}
          emitOnExit={emitOnExit}
          emitOnExitWithCanDo={emitOnExitWithCanDo}
        />
      )}
    </div>
  );
};

export default PopUpSummary;
